import React from "react";
import { AlertTriangle } from "lucide-react";

const CardEditPopup = ({
  newItem,
  setNewItem,
  savedQueries,
  setLoader,
  handleDateFieldsUpdate,
  getData,
  getThemeURL,
  selectedDefaultBankId,
}) => {
  const [noDataForDateRange, setNoDataForDateRange] = React.useState(false);

  const labelStyle = {
    display: "block",
    marginBottom: "8px",
    fontSize: "14px",
    fontWeight: "bold",
    color: "#374151",
  };

  // Helper function to extract clean column names from aggregated functions
  const extractColumnName = (columnStr) => {
    // Check if it's an aggregated function (SUM, MIN, MAX, AVG, COUNT)
    const aggPattern = /^(SUM|MIN|MAX|AVG|COUNT)\((.+?)\)$/i;
    const match = columnStr.match(aggPattern);

    if (match) {
      // Extract the column part (e.g., "loan_disbursed.loan_amount")
      const fullColumn = match[2];
      // Return only the column name after the dot
      return fullColumn.includes(".")
        ? fullColumn.split(".").pop()
        : fullColumn;
    }

    // If it's a regular column (e.g., "loan_disbursed.loan_amount")
    if (columnStr.includes(".")) {
      return columnStr.split(".").pop();
    }

    // Return as-is if no pattern matches
    return columnStr;
  };

  // Helper function to determine filter fields dynamically
  const determineFilterFields = (queryObject) => {
    // Extract table name from entity
    const entity = queryObject.entity || "";
    const tableName = entity.includes(".") ? entity.split(".")[1] : entity;

    // Get joins information
    const joins = queryObject.joins || [];
    const joinedTables = joins.map((join) => {
      const table = join.table || join.alias || "";
      return table.includes(".") ? table.split(".")[1] : table;
    });

    // Helper function to determine filter field names
    let bankFilterField = null;

    // Check what tables are available
    const hasBankTable = tableName === "bank" || joinedTables.includes("bank");
    const hasJoins = joins.length > 0;

    console.log("CardEditPopup: Table analysis:", {
      tableName,
      joinedTables,
      hasBankTable,
      hasJoins,
    });

    // Determine bank filter field
    if (tableName === "bank") {
      // If entity is bank table
      bankFilterField = "bank.id";
    } else if (hasBankTable && hasJoins) {
      // If joins are there and bank table is available
      bankFilterField = "bank.id";
    } else if (
      !hasBankTable &&
      tableName !== "bank" &&
      tableName !== "branch_details"
    ) {
      // For core tables (not bank or branch_details), use table.bank_id
      bankFilterField = `${tableName}.bank_id`;
    } else if (!hasBankTable) {
      // If no bank table available, use current table with bank_id
      bankFilterField = `${tableName}.bank_id`;
    }

    console.log(
      "CardEditPopup: Determined bank filter field:",
      bankFilterField
    );

    return { bankFilterField };
  };

  // Add this function inside CardEditPopup component
  const getDateFields = () => {
    if (!newItem.data || newItem.data.length === 0) {
      return [];
    }

    const dateFields = [];
    const sampleRow = newItem.data[0];

    Object.keys(sampleRow).forEach((key) => {
      const value = sampleRow[key];

      // Check if the value looks like a date or timestamp
      if (typeof value === "string") {
        // Check for various date formats
        const datePatterns = [
          /^\d{4}-\d{2}-\d{2}$/, // YYYY-MM-DD (date)
          /^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/, // ISO timestamp
          /^\d{4}-\d{2}-\d{2}\s\d{2}:\d{2}:\d{2}/, // YYYY-MM-DD HH:MM:SS
        ];

        const isDateField = datePatterns.some((pattern) => pattern.test(value));

        if (isDateField) {
          // Determine the full column name (with table prefix)
          const tableName = newItem.dataSource
            ? savedQueries.find((q) => q.serviceName === newItem.dataSource)
                ?.queryBuilder
            : null;

          let fullColumnName = key;
          if (tableName) {
            try {
              const queryObject =
                typeof tableName === "string"
                  ? JSON.parse(tableName)
                  : tableName;
              const tablePrefix = queryObject.entity.split(".")[1];
              fullColumnName = `${tablePrefix}.${key}`;
            } catch (error) {
              console.warn("Could not parse table name:", error);
            }
          }

          dateFields.push({
            key: key,
            fullName: fullColumnName,
            label: key
              .replace(/([A-Z])/g, " $1")
              .replace(/^./, (str) => str.toUpperCase()),
            value: value,
            type:
              value.includes("T") || value.includes(" ") ? "timestamp" : "date",
          });
        }
      }
    });

    return dateFields;
  };

  const dateFields = getDateFields();

  const handleRefreshWithDateFilter = async () => {
    if (
      !newItem.dataSource ||
      !newItem.fromDate ||
      !newItem.toDate ||
      !newItem.selectedDateField
    ) {
      return;
    }

    setLoader(true);

    const matchedQuery = savedQueries.find(
      (q) => q.serviceName === newItem.dataSource
    );
    if (!matchedQuery) {
      setLoader(false);
      return;
    }

    const queryObject =
      typeof matchedQuery.queryBuilder === "string"
        ? JSON.parse(matchedQuery.queryBuilder)
        : matchedQuery.queryBuilder;

    const filters = {};

    try {
      const userResponse = await getData(getThemeURL);
      if (
        userResponse &&
        Array.isArray(userResponse) &&
        userResponse.length > 0
      ) {
        const user = userResponse[0];

        // **NEW**: Apply dynamic filter logic for date filter refresh
        if (user.role_name === "SUPER ADMIN") {
          // For Super Admin, use selectedBankIds if available
          if (selectedDefaultBankId) {
            // NEW Use dynamic filter field determination
            const { bankFilterField } = determineFilterFields(queryObject);
            if (bankFilterField) {
              filters[bankFilterField] = {
                operator: "IN",
                value: [Number(selectedDefaultBankId)], // Convert single ID to array format
              };
              console.log(
                "Applied dynamic bank filter for Super Admin in date filter:",
                filters[bankFilterField]
              );
            }
          }
        } else {
          // For non-Super Admin users, use their specific IDs with dynamic field determination
          const { bankFilterField } = determineFilterFields(queryObject);

          if (user.bank_id && bankFilterField) {
            filters[bankFilterField] = {
              operator: "IN",
              value: [user.bank_id], // Convert single ID to array format
            };
            console.log(
              "Applied dynamic bank filter for regular user in date filter:",
              filters[bankFilterField]
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user profile for filters:", error);
    }

    // Create date filter with timestamp handling
    const dateFilter = {};

    // Check if the selected field is a timestamp type
    const selectedFieldData = dateFields.find(
      (field) => field.fullName === newItem.selectedDateField
    );
    const isTimestamp =
      selectedFieldData && selectedFieldData.type === "timestamp";
    handleDateFieldsUpdate(dateFields);

    if (isTimestamp) {
      // Append timestamp to fromDate and toDate for timestamp fields with space
      const fromDateTime = newItem.fromDate + " 00:00:00.000";
      const toDateTime = newItem.toDate + " 23:59:59.999"; // Use end of day for toDate

      dateFilter[newItem.selectedDateField] = {
        operator: "BETWEEN",
        value: [fromDateTime, toDateTime],
      };
    } else {
      // Use date values as-is for date-only fields
      dateFilter[newItem.selectedDateField] = {
        operator: "BETWEEN",
        value: [newItem.fromDate, newItem.toDate],
      };
    }

    const enhancedQuery = {
      ...queryObject,
      filters: {
        ...queryObject.filters,
        ...dateFilter,
        ...filters,
      },
    };

    // Fetch user profile to determine auth parameters
    const userResponse = await getData(getThemeURL);
    if (
      !userResponse ||
      !Array.isArray(userResponse) ||
      userResponse.length === 0
    ) {
      console.error("Failed to fetch user profile");
      setLoader(false);
      return;
    }

    const user = userResponse[0];
    const roleName = user.role_name;
    const userBankId = user.bank_id;

    const authObject = {
      instanceUrl: "/query-builder-api/execute",
      apiType: "POST",
    };

    if (roleName === "SUPER ADMIN") {
      authObject.type = "SA";
      // Use selectedDefaultBankId for SA instead of instance data
      authObject.bank_id = selectedDefaultBankId
        ? [Number(selectedDefaultBankId)]
        : [];
    } else if (user.is_admin === true) {
      authObject.type = "HO";
      authObject.bank_id = userBankId ? [userBankId] : [];
    } else {
      authObject.type = "U";
      authObject.bank_id = userBankId ? [userBankId] : [];
    }

    // Prepare the final payload
    const payloadWithAuth = {
      auth: authObject,
      main: enhancedQuery,
    };

    console.log(
      "CardEditPopup: Query sent to /nifi/fetchQuery with date filter:",
      enhancedQuery
    );
    console.log("CardEditPopup: Applied filters with date:", {
      ...filters,
      ...dateFilter,
    });

    // fetch with new filter
    try {
      const response = await fetch("/nifi/fetchQuery-API", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadWithAuth),
      });

      const result = await response.json();

      // Update this part to handle the new response structure
      const responseData = result.aggregatedData.data || [];

      if (responseData.length === 0) {
        setNoDataForDateRange(true);
        return;
      } else {
        setNoDataForDateRange(false);
      }

      if (responseData && responseData.length > 0) {
        const availableFields = Object.keys(responseData[0] || {});
        const hiddenIdFields = ["bank_id", "branchdetails_id", "branch_id"];
        const configurableFields = availableFields.filter(
          (field) => !hiddenIdFields.includes(field.toLowerCase())
        );

        const initialFieldConfigs = configurableFields.map((field) => ({
          fieldName: field,
          color: "#000000",
          fontSize: "small",
          fontSizeValue: 12,
          textAlignment: "left",
          enabled: true,
        }));

        const fieldsConfig = configurableFields.map((field) => ({
          fieldName: field,
          enabled: true,
        }));

        const cardStyles = {};
        initialFieldConfigs.forEach((config) => {
          if (config.enabled) {
            cardStyles[`${config.fieldName}_color`] = config.color;
            cardStyles[`${config.fieldName}_fontSize`] = config.fontSizeValue;
            cardStyles[`${config.fieldName}_textAlignment`] =
              config.textAlignment;
          }
        });

        const enabledConfigurableFields = fieldsConfig
          .filter((config) => config.enabled)
          .map((config) => config.fieldName);
        const idFields = availableFields.filter((field) =>
          hiddenIdFields.includes(field.toLowerCase())
        );
        const allIncludedFields = [...enabledConfigurableFields, ...idFields];

        const filteredData = responseData.map((item) => {
          const filteredItem = {};
          allIncludedFields.forEach((field) => {
            if (item.hasOwnProperty(field)) {
              filteredItem[field] = item[field];
            }
          });
          return filteredItem;
        });

        const cardDataSourceConfig = {
          data: filteredData,
          columns: result.aggregatedData.columns || availableFields,
          serviceName: newItem.dataSource,
          availableFields: availableFields,
          configurableFields: configurableFields,
          hiddenIdFields: idFields,
          originalData: responseData,
        };

        setNewItem({
          ...newItem,
          data: filteredData,
          cardStyles: cardStyles,
          fieldsConfig: fieldsConfig,
          dateFilterApplied: true,
          dataSourceConfig: cardDataSourceConfig,
          value: "",
          valueKey: "",
          fieldConfigs: initialFieldConfigs,
          allAvailableFields: availableFields,
        });
      } else {
        setNewItem({
          ...newItem,
          data: [],
          cardStyles: {},
          fieldsConfig: [],
          dataSourceConfig: null,
          value: "",
          valueKey: "",
          fieldConfigs: [],
          allAvailableFields: [],
        });
      }
    } catch (error) {
      console.error("Error fetching card data source:", error);
      setNewItem({
        ...newItem,
        data: [],
        cardStyles: {},
        fieldsConfig: [],
        dataSourceConfig: null,
        value: "",
        valueKey: "",
        fieldConfigs: [],
        allAvailableFields: [],
      });
    } finally {
      setLoader(false);
    }
  };

  const handleDataSourceChange = async (serviceName) => {
    setLoader(true);
    if (!serviceName) {
      setNewItem({
        ...newItem,
        dataSource: "",
        data: [],
        cardStyles: {},
        fieldsConfig: [],
        value: "",
        valueKey: "",
        fieldConfigs: [],
        allAvailableFields: [],
        fromDate: "",
        toDate: "",
        dateFilterApplied: false,
        dateFieldsChanged: false,
      });
      return;
    }

    const matchedQuery = savedQueries.find(
      (q) => q.serviceName === serviceName
    );
    if (!matchedQuery) {
      setLoader(false);
      return;
    }

    const queryObject =
      typeof matchedQuery.queryBuilder === "string"
        ? JSON.parse(matchedQuery.queryBuilder)
        : matchedQuery.queryBuilder;

    const filters = {};

    try {
      const userResponse = await getData(getThemeURL);
      if (
        userResponse &&
        Array.isArray(userResponse) &&
        userResponse.length > 0
      ) {
        const user = userResponse[0];

        // **NEW**: Apply dynamic filter logic for data source selection
        if (user.role_name === "SUPER ADMIN") {
          // For Super Admin, use selectedDefaultBankId
          if (selectedDefaultBankId) {
            const { bankFilterField } = determineFilterFields(queryObject);
            if (bankFilterField) {
              filters[bankFilterField] = {
                operator: "IN",
                value: [Number(selectedDefaultBankId)],
              };
              console.log(
                "Applied dynamic bank filter for Super Admin:",
                filters[bankFilterField]
              );
            }
          }
        } else {
          // For non-Super Admin users, use their specific IDs with dynamic field determination
          const { bankFilterField } = determineFilterFields(queryObject);

          if (user.bank_id && bankFilterField) {
            filters[bankFilterField] = {
              operator: "IN",
              value: [user.bank_id], // Convert single ID to array format
            };
            console.log(
              "Applied dynamic bank filter for regular user:",
              filters[bankFilterField]
            );
          }
        }
      }
    } catch (error) {
      console.error("Error fetching user profile for filters:", error);
    }

    const enhancedQuery = {
      ...queryObject,
      filters: {
        ...queryObject.filters,
        ...filters,
      },
    };

    // Fetch user profile to determine auth parameters
    const userResponse = await getData(getThemeURL);
    if (
      !userResponse ||
      !Array.isArray(userResponse) ||
      userResponse.length === 0
    ) {
      console.error("Failed to fetch user profile");
      setLoader(false);
      return;
    }

    const user = userResponse[0];
    const roleName = user.role_name;
    const userBankId = user.bank_id;

    const authObject = {
      instanceUrl: "/query-builder-api/execute",
      apiType: "POST",
    };

    if (roleName === "SUPER ADMIN") {
      authObject.type = "SA";
      // Use selectedDefaultBankId for SA instead of instance data
      authObject.bank_id = selectedDefaultBankId
        ? [Number(selectedDefaultBankId)]
        : [];
    } else if (user.is_admin === true) {
      authObject.type = "HO";
      authObject.bank_id = userBankId ? [userBankId] : [];
    } else {
      authObject.type = "U";
      authObject.bank_id = userBankId ? [userBankId] : [];
    }

    // Prepare the final payload
    const payloadWithAuth = {
      auth: authObject,
      main: enhancedQuery,
    };

    console.log(
      "CardEditPopup: Query sent to /nifi/fetchQuery:",
      payloadWithAuth
    );
    console.log("CardEditPopup: Applied filters:", filters);

    try {
      const response = await fetch("/nifi/fetchQuery-API", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payloadWithAuth),
      });

      console.log("now here");

      const result = await response.json();
      console.log("Card Data Source - Executed Query Result:", result);
      // Update this part to handle the new response structure
      const responseData = result.aggregatedData.data || [];

      if (responseData && responseData.length > 0) {
        const availableFields = Object.keys(responseData[0] || {});
        const hiddenIdFields = ["bank_id", "branchdetails_id", "branch_id"];
        const configurableFields = availableFields.filter(
          (field) => !hiddenIdFields.includes(field.toLowerCase())
        );

        const initialFieldConfigs = configurableFields.map((field) => ({
          fieldName: field,
          color: "#000000",
          fontSize: "small",
          fontSizeValue: 12,
          textAlignment: "left",
          enabled: true,
        }));

        const fieldsConfig = configurableFields.map((field) => ({
          fieldName: field,
          enabled: true,
        }));

        const cardStyles = {};
        initialFieldConfigs.forEach((config) => {
          if (config.enabled) {
            cardStyles[`${config.fieldName}_color`] = config.color;
            cardStyles[`${config.fieldName}_fontSize`] = config.fontSizeValue;
            cardStyles[`${config.fieldName}_textAlignment`] =
              config.textAlignment;
          }
        });

        const enabledConfigurableFields = fieldsConfig
          .filter((config) => config.enabled)
          .map((config) => config.fieldName);
        const idFields = availableFields.filter((field) =>
          hiddenIdFields.includes(field.toLowerCase())
        );
        const allIncludedFields = [...enabledConfigurableFields, ...idFields];

        const filteredData = responseData.map((item) => {
          const filteredItem = {};
          allIncludedFields.forEach((field) => {
            if (item.hasOwnProperty(field)) {
              filteredItem[field] = item[field];
            }
          });
          return filteredItem;
        });

        const cardDataSourceConfig = {
          data: filteredData,
          columns: result.aggregatedData.columns || availableFields,
          serviceName: serviceName,
          availableFields: availableFields,
          configurableFields: configurableFields,
          hiddenIdFields: idFields,
          originalData: responseData,
        };

        setNewItem({
          ...newItem,
          dataSource: serviceName,
          data: filteredData,
          cardStyles: cardStyles,
          fieldsConfig: fieldsConfig,
          dataSourceConfig: cardDataSourceConfig,
          value: "",
          valueKey: "",
          fieldConfigs: initialFieldConfigs,
          allAvailableFields: availableFields,
          fromDate: "",
          toDate: "",
        });
      } else {
        // Handle empty data case - extract columns from columns array
        const columns = result.aggregatedData.columns || [];
        const cleanColumns = columns.map((col) => extractColumnName(col));

        console.log("Card - Empty data, extracted columns:", cleanColumns);

        // For empty data, create minimal field configs from column names
        const configurableFields = cleanColumns;
        const initialFieldConfigs = configurableFields.map((field) => ({
          fieldName: field,
          color: "#000000",
          fontSize: "small",
          fontSizeValue: 12,
          textAlignment: "left",
          enabled: true,
        }));

        const fieldsConfig = configurableFields.map((field) => ({
          fieldName: field,
          enabled: true,
        }));

        setNewItem({
          ...newItem,
          dataSource: serviceName,
          data: [],
          cardStyles: {},
          fieldsConfig: fieldsConfig,
          dataSourceConfig: null,
          value: "",
          valueKey: "",
          fieldConfigs: initialFieldConfigs,
          allAvailableFields: configurableFields,
          fromDate: "",
          toDate: "",
        });
      }
    } catch (error) {
      console.error("Error fetching card data source:", error);
      setNewItem({
        ...newItem,
        dataSource: "",
        data: [],
        cardStyles: {},
        fieldsConfig: [],
        dataSourceConfig: null,
        value: "",
        valueKey: "",
        fieldConfigs: [],
        allAvailableFields: [],
        fromDate: "",
        toDate: "",
      });
    } finally {
      setLoader(false);
    }
  };

  const handleFieldConfigChange = (index, updates) => {
    const updatedFieldConfigs = newItem.fieldConfigs.map((config, i) =>
      i === index ? { ...config, ...updates } : config
    );

    const fieldName = newItem.fieldConfigs[index].fieldName;
    const updatedCardStyles = { ...newItem.cardStyles };

    if (updates.enabled !== undefined) {
      const updatedFieldsConfig = newItem.fieldsConfig.map((config) =>
        config.fieldName === fieldName
          ? { ...config, enabled: updates.enabled }
          : config
      );

      if (updates.enabled) {
        updatedCardStyles[`${fieldName}_color`] =
          updatedFieldConfigs[index].color;
        updatedCardStyles[`${fieldName}_fontSize`] =
          updatedFieldConfigs[index].fontSizeValue;
        updatedCardStyles[`${fieldName}_textAlignment`] =
          updatedFieldConfigs[index].textAlignment;
      } else {
        delete updatedCardStyles[`${fieldName}_color`];
        delete updatedCardStyles[`${fieldName}_fontSize`];
        delete updatedCardStyles[`${fieldName}_textAlignment`];
      }

      // Rebuild data array
      const originalData = newItem.dataSourceConfig?.originalData || [];
      const hiddenIdFields = newItem.dataSourceConfig?.hiddenIdFields || [];
      const enabledConfigurableFields = updatedFieldsConfig
        .filter((config) => config.enabled)
        .map((config) => config.fieldName);
      const allIncludedFields = [
        ...enabledConfigurableFields,
        ...hiddenIdFields,
      ];

      const filteredData = originalData.map((item) => {
        const filteredItem = {};
        allIncludedFields.forEach((field) => {
          if (item.hasOwnProperty(field)) {
            filteredItem[field] = item[field];
          }
        });
        return filteredItem;
      });

      setNewItem({
        ...newItem,
        fieldConfigs: updatedFieldConfigs,
        fieldsConfig: updatedFieldsConfig,
        data: filteredData,
        cardStyles: updatedCardStyles,
      });
    } else {
      // Handle style updates
      if (updatedFieldConfigs[index].enabled) {
        if (updates.color)
          updatedCardStyles[`${fieldName}_color`] = updates.color;
        if (updates.fontSizeValue)
          updatedCardStyles[`${fieldName}_fontSize`] = updates.fontSizeValue;
        if (updates.textAlignment)
          updatedCardStyles[`${fieldName}_textAlignment`] =
            updates.textAlignment;
      }

      setNewItem({
        ...newItem,
        fieldConfigs: updatedFieldConfigs,
        cardStyles: updatedCardStyles,
      });
    }
  };

  return (
    <>
      {/* Data Source Selection */}
      <div style={{ marginBottom: "16px" }}>
        <label style={labelStyle}>
          Data Source<span style={{ color: "red" }}>*</span>
        </label>
        <select
          value={newItem.dataSource || ""}
          onChange={(e) => handleDataSourceChange(e.target.value)}
          style={{
            width: "95%",
            padding: "10px 12px",
            borderRadius: "8px",
            border: "1px solid #d1d5db",
            backgroundColor: "white",
            fontSize: "14px",
            color: "#374151",
            outline: "none",
          }}
        >
          <option value="">Select data source</option>
          {savedQueries.map((query) => (
            <option key={query.id} value={query.serviceName}>
              {query.serviceName}
            </option>
          ))}
        </select>
        {!newItem.dataSource && (
          <span
            style={{
              color: "#ef4444",
              fontSize: "12px",
              marginTop: "4px",
              display: "block",
            }}
          >
            Data source is required
          </span>
        )}
      </div>

      {/* Field Configuration Section */}
      {newItem.dataSource &&
        newItem.fieldConfigs &&
        newItem.fieldConfigs.length > 0 && (
          <div style={{ marginBottom: "16px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "12px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              Configure Card Fields
            </label>
            <div
              style={{
                maxHeight: "300px",
                overflowY: "auto",
                border: "1px solid #e5e7eb",
                borderRadius: "8px",
                padding: "12px",
                backgroundColor: "#f9fafb",
              }}
            >
              {newItem.fieldConfigs.map((fieldConfig, index) => (
                <div
                  key={index}
                  style={{
                    marginBottom: "16px",
                    padding: "12px",
                    backgroundColor: "white",
                    borderRadius: "6px",
                    border: "1px solid #e5e7eb",
                  }}
                >
                  {/* Field Enable/Disable Toggle */}
                  <div
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginBottom: "12px",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={fieldConfig.enabled}
                      onChange={(e) =>
                        handleFieldConfigChange(index, {
                          enabled: e.target.checked,
                        })
                      }
                      style={{
                        width: "16px",
                        height: "16px",
                        marginRight: "8px",
                        accentColor: "#1a7a73",
                        cursor: "pointer",
                      }}
                    />
                    <label
                      style={{
                        fontSize: "14px",
                        fontWeight: "600",
                        color: fieldConfig.enabled ? "#374151" : "#9ca3af",
                        cursor: "pointer",
                      }}
                    >
                      {fieldConfig.fieldName}
                    </label>
                  </div>

                  {/* Field Configuration Options */}
                  {fieldConfig.enabled && (
                    <div
                      style={{
                        display: "flex",
                        gap: "12px",
                        alignItems: "flex-start",
                        flexWrap: "wrap",
                      }}
                    >
                      {/* Color Selection */}
                      <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            color: "#6b7280",
                          }}
                        >
                          Text Color
                        </label>
                        <div
                          style={{
                            display: "flex",
                            alignItems: "center",
                            gap: "6px",
                          }}
                        >
                          <input
                            type="color"
                            value={fieldConfig.color}
                            onChange={(e) =>
                              handleFieldConfigChange(index, {
                                color: e.target.value,
                              })
                            }
                            style={{
                              width: "25px",
                              height: "25px",
                              border: "1px solid #d1d5db",
                              borderRadius: "4px",
                              cursor: "pointer",
                              backgroundColor: "transparent",
                            }}
                          />
                          <input
                            type="text"
                            value={fieldConfig.color}
                            onChange={(e) =>
                              handleFieldConfigChange(index, {
                                color: e.target.value,
                              })
                            }
                            style={{
                              flex: 1,
                              padding: "4px 6px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              fontSize: "11px",
                              outline: "none",
                            }}
                          />
                        </div>
                      </div>

                      {/* Font Size Selection */}
                      <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            color: "#6b7280",
                          }}
                        >
                          Font Size
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "6px",
                            alignItems: "center",
                          }}
                        >
                          <select
                            value={fieldConfig.fontSize}
                            onChange={(e) => {
                              const fontSizeOption = e.target.value;
                              let fontSizeValue;

                              switch (fontSizeOption) {
                                case "small":
                                  fontSizeValue = 12;
                                  break;
                                case "medium":
                                  fontSizeValue = 16;
                                  break;
                                case "large":
                                  fontSizeValue = 20;
                                  break;
                                case "extra-large":
                                  fontSizeValue = 24;
                                  break;
                                case "custom":
                                  fontSizeValue =
                                    fieldConfig.fontSizeValue || 12;
                                  break;
                                default:
                                  fontSizeValue = 12;
                              }

                              handleFieldConfigChange(index, {
                                fontSize: fontSizeOption,
                                fontSizeValue,
                              });
                            }}
                            style={{
                              width: "90px",
                              padding: "4px 6px",
                              borderRadius: "4px",
                              border: "1px solid #d1d5db",
                              fontSize: "11px",
                              outline: "none",
                            }}
                          >
                            <option value="small">Small</option>
                            <option value="medium">Medium</option>
                            <option value="large">Large</option>
                            <option value="extra-large">X-Large</option>
                            <option value="custom">Custom</option>
                          </select>

                          {fieldConfig.fontSize === "custom" && (
                            <input
                              type="number"
                              value={fieldConfig.fontSizeValue || 12}
                              onChange={(e) =>
                                handleFieldConfigChange(index, {
                                  fontSizeValue: parseInt(e.target.value) || 12,
                                })
                              }
                              placeholder="px"
                              min="8"
                              max="72"
                              style={{
                                width: "50px",
                                padding: "4px 6px",
                                borderRadius: "4px",
                                border: "1px solid #d1d5db",
                                fontSize: "11px",
                                outline: "none",
                              }}
                            />
                          )}
                        </div>
                      </div>

                      {/* Text Alignment Selection */}
                      <div style={{ flex: "1 1 150px", minWidth: "120px" }}>
                        <label
                          style={{
                            display: "block",
                            marginBottom: "6px",
                            fontSize: "12px",
                            fontWeight: "500",
                            color: "#6b7280",
                          }}
                        >
                          Text Alignment
                        </label>
                        <div
                          style={{
                            display: "flex",
                            gap: "4px",
                            border: "1px solid #d1d5db",
                            borderRadius: "4px",
                            padding: "2px",
                            backgroundColor: "white",
                          }}
                        >
                          {[
                            { value: "left", label: "L" },
                            { value: "center", label: "C" },
                            { value: "right", label: "R" },
                          ].map((alignment) => (
                            <button
                              key={alignment.value}
                              type="button"
                              onClick={() =>
                                handleFieldConfigChange(index, {
                                  textAlignment: alignment.value,
                                })
                              }
                              style={{
                                flex: 1,
                                padding: "4px 2px",
                                borderRadius: "2px",
                                border: "none",
                                backgroundColor:
                                  fieldConfig.textAlignment === alignment.value
                                    ? "#1a7a73"
                                    : "transparent",
                                color:
                                  fieldConfig.textAlignment === alignment.value
                                    ? "white"
                                    : "#6b7280",
                                cursor: "pointer",
                                fontSize: "10px",
                                fontWeight: "500",
                                transition: "all 0.2s",
                              }}
                              onMouseOver={(e) => {
                                if (
                                  fieldConfig.textAlignment !== alignment.value
                                ) {
                                  e.currentTarget.style.backgroundColor =
                                    "#f3f4f6";
                                }
                              }}
                              onMouseOut={(e) => {
                                if (
                                  fieldConfig.textAlignment !== alignment.value
                                ) {
                                  e.currentTarget.style.backgroundColor =
                                    "transparent";
                                }
                              }}
                            >
                              {alignment.label}
                            </button>
                          ))}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

      {/* Data Filtering Based on Date Range Section - UPDATED WITH CONSISTENT LAYOUT */}
      <div style={{ marginBottom: "24px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "16px",
            fontSize: "16px",
            fontWeight: "bold",
            color: "#374151",
          }}
        >
          Data filtering based on date range
        </label>

        <div
          style={{
            display: "flex",
            gap: "16px",
            alignItems: "flex-start", // Changed from "flex-end" to "flex-start"
            flexWrap: "wrap",
          }}
        >
          {/* Date Field Selector Dropdown */}
          <div style={{ flex: 1, minWidth: "180px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              Date Field
            </label>
            <select
              value={newItem.selectedDateField || ""}
              onChange={(e) => {
                setNewItem({
                  ...newItem,
                  selectedDateField: e.target.value,
                  dateFilterApplied: false,
                  dateFieldsChanged: true,
                  fromDate: "",
                  toDate: "",
                  dateValidationError: null,
                  toDateValidationError: null,
                });
                setNoDataForDateRange(false);
              }}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: "white",
              }}
            >
              <option value="">Select date field</option>
              {dateFields.map((field) => (
                <option key={field.fullName} value={field.fullName}>
                  {field.label}
                </option>
              ))}
            </select>

            {/* Reserved space for consistency */}
            <div
              style={{
                height: "32px", // Fixed height to prevent layout shifts
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {/* Empty space for consistency */}
            </div>
          </div>

          {/* From Date */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              From Date
            </label>
            <input
              type="date"
              value={newItem.fromDate || ""}
              disabled={!newItem.selectedDateField}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = new Date().toISOString().split("T")[0];

                if (selectedDate > today) {
                  const updatedItem = {
                    ...newItem,
                    fromDate: today,
                    dateFieldsChanged: true,
                    dateValidationError:
                      "Future dates are not allowed. Date has been set to today.",
                  };

                  if (newItem.toDate && today > newItem.toDate) {
                    updatedItem.toDate = "";
                    updatedItem.toDateValidationError =
                      "To Date cleared because From Date was adjusted to today.";
                  }

                  setNewItem(updatedItem);

                  setTimeout(() => {
                    setNewItem((prev) => ({
                      ...prev,
                      dateValidationError: null,
                    }));
                  }, 3000);

                  return;
                }

                const updatedItem = {
                  ...newItem,
                  fromDate: selectedDate,
                  dateValidationError: null,
                  dateFilterApplied: false,
                  dateFieldsChanged: true,
                };

                if (newItem.toDate && newItem.toDate < selectedDate) {
                  updatedItem.toDate = "";
                  updatedItem.toDateValidationError =
                    "To Date cleared because it was earlier than From Date.";
                }

                setNewItem(updatedItem);
                setNoDataForDateRange(false);
              }}
              max={new Date().toISOString().split("T")[0]}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: newItem.dateValidationError
                  ? "1px solid #ef4444"
                  : "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: !newItem.selectedDateField
                  ? "#f3f4f6"
                  : "white",
                cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
              onBlur={(e) =>
                (e.target.style.borderColor = newItem.dateValidationError
                  ? "#ef4444"
                  : "#d1d5db")
              }
            />

            {/* Reserved space for error message */}
            <div
              style={{
                height: "32px",
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {newItem.dateValidationError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  {newItem.dateValidationError}
                </span>
              )}
            </div>
          </div>

          {/* To Date */}
          <div style={{ flex: 1, minWidth: "150px" }}>
            <label
              style={{
                display: "block",
                marginBottom: "8px",
                fontSize: "14px",
                fontWeight: "bold",
                color: "#374151",
              }}
            >
              To Date
            </label>
            <input
              type="date"
              value={newItem.toDate || ""}
              disabled={!newItem.selectedDateField}
              onChange={(e) => {
                const selectedDate = e.target.value;
                const today = new Date().toISOString().split("T")[0];

                if (selectedDate > today) {
                  setNewItem({
                    ...newItem,
                    toDate: today,
                    dateFieldsChanged: true,
                    toDateValidationError:
                      "Future dates are not allowed. Date has been set to today.",
                  });

                  setTimeout(() => {
                    setNewItem((prev) => ({
                      ...prev,
                      toDateValidationError: null,
                    }));
                  }, 3000);

                  return;
                }

                if (newItem.fromDate && selectedDate < newItem.fromDate) {
                  setNewItem({
                    ...newItem,
                    toDate: selectedDate,
                    dateFieldsChanged: true,
                    toDateValidationError:
                      "To Date cannot be earlier than From Date.",
                  });
                  return;
                }

                setNewItem({
                  ...newItem,
                  toDate: selectedDate,
                  dateFieldsChanged: true,
                  dateFilterApplied: false,
                  toDateValidationError: null,
                  dateValidationError: null,
                });
                setNoDataForDateRange(false);
              }}
              max={new Date().toISOString().split("T")[0]}
              // Remove min as discussed previously
              // min={newItem.fromDate || ""}
              style={{
                width: "100%",
                padding: "10px 12px",
                borderRadius: "6px",
                border: newItem.toDateValidationError
                  ? "1px solid #ef4444"
                  : "1px solid #d1d5db",
                fontSize: "14px",
                height: "40px",
                boxSizing: "border-box",
                backgroundColor: !newItem.selectedDateField
                  ? "#f3f4f6"
                  : "white", // Add visual indication when disabled
                cursor: !newItem.selectedDateField ? "not-allowed" : "pointer",
              }}
              onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
              onBlur={(e) =>
                (e.target.style.borderColor = newItem.toDateValidationError
                  ? "#ef4444"
                  : "#d1d5db")
              }
            />

            {/* Reserved space for error message */}
            <div
              style={{
                height: "32px",
                display: "flex",
                alignItems: "flex-start",
                paddingTop: "4px",
              }}
            >
              {newItem.toDateValidationError && (
                <span
                  style={{
                    color: "#ef4444",
                    fontSize: "12px",
                    lineHeight: "16px",
                  }}
                >
                  {newItem.toDateValidationError}
                </span>
              )}
            </div>
          </div>

          {/* Update Filter Button - Positioned to align with input fields */}
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              justifyContent: "flex-start",
            }}
          >
            {/* Invisible label for alignment */}
            <div style={{ height: "24px", marginBottom: "8px" }}></div>

            <button
              onClick={handleRefreshWithDateFilter}
              disabled={
                !newItem.dataSource ||
                !newItem.fromDate ||
                !newItem.toDate ||
                !newItem.selectedDateField ||
                newItem.dateValidationError || // Add validation error check
                newItem.toDateValidationError // Add validation error check
              }
              style={{
                padding: "12px 20px",
                borderRadius: "6px",
                border: "1px solid #d1d5db",
                backgroundColor:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.dateValidationError || // Add validation error check
                  newItem.toDateValidationError // Add validation error check
                    ? "#f3f4f6"
                    : "#1a7a73",
                color:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.dateValidationError || // Add validation error check
                  newItem.toDateValidationError // Add validation error check
                    ? "#9ca3af"
                    : "white",
                cursor:
                  !newItem.dataSource ||
                  !newItem.fromDate ||
                  !newItem.toDate ||
                  !newItem.selectedDateField ||
                  newItem.dateValidationError || // Add validation error check
                  newItem.toDateValidationError // Add validation error check
                    ? "not-allowed"
                    : "pointer",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                fontSize: "14px",
                fontWeight: "500",
                height: "40px",
                transition: "all 0.2s ease",
                whiteSpace: "nowrap",
              }}
              title={
                newItem.dateValidationError || newItem.toDateValidationError
                  ? "Please fix date validation errors before applying filter"
                  : "Apply date filter and refresh data"
              }
            >
              Update Filter
            </button>

            {/* Reserved space to match other fields */}
            <div style={{ height: "32px" }}></div>
          </div>
        </div>

        {/* Warning message for unapplied date filters */}
        {newItem.fromDate &&
          newItem.toDate &&
          newItem.selectedDateField &&
          !newItem.dateFilterApplied &&
          newItem.dateFieldsChanged &&
          !newItem.dateValidationError && // Only show when no validation errors
          !newItem.toDateValidationError &&
          !noDataForDateRange && (
            <div
              style={{
                marginTop: "12px",
                padding: "12px 16px",
                backgroundColor: "#fef3c7",
                border: "1px solid #f59e0b",
                borderRadius: "6px",
                color: "#92400e",
                fontSize: "14px",
                display: "flex",
                alignItems: "center",
                gap: "8px",
                width: "100%",
              }}
            >
              <AlertTriangle size={16} />
              Click "Update Filter" to apply the date range before saving the
              widget.
            </div>
          )}

        {/* Date Field Selection Validation Error */}
        {newItem.fromDate && newItem.toDate && !newItem.selectedDateField && (
          <div
            style={{
              marginTop: "12px",
              padding: "12px 16px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <AlertTriangle size={16} />
            Please select a date field to filter by.
          </div>
        )}

        {/* Date Range Validation Error */}
        {newItem.fromDate && !newItem.toDate && (
          <div
            style={{
              marginTop: "12px",
              padding: "8px 12px",
              backgroundColor: "#fef2f2",
              border: "1px solid #fecaca",
              borderRadius: "6px",
              color: "#dc2626",
              fontSize: "14px",
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <AlertTriangle size={16} />
            Please select both From Date and To Date to create a valid date
            range.
          </div>
        )}
      </div>
      {noDataForDateRange && (
        <div
          style={{
            marginTop: "12px",
            padding: "12px 16px",
            backgroundColor: "#f0f9ff",
            border: "1px solid #1a7a73",
            borderRadius: "6px",
            color: "#0f4a45",
            fontSize: "14px",
            display: "flex",
            alignItems: "center",
            gap: "8px",
          }}
        >
          <AlertTriangle size={16} />
          No data found for the selected date range.
        </div>
      )}
      {/* Card Color */}
      <div style={{ marginBottom: "16px" }}>
        <label
          style={{
            display: "block",
            marginBottom: "8px",
            fontSize: "14px",
            fontWeight: "500",
            color: "#374151",
          }}
        >
          Card Color
        </label>
        <div
          style={{
            display: "flex",
            alignItems: "center",
            gap: "12px",
          }}
        >
          <input
            type="color"
            value={newItem.color || "#ffffff"}
            onChange={(e) => setNewItem({ ...newItem, color: e.target.value })}
            style={{
              width: "50px",
              height: "40px",
              border: "1px solid #d1d5db",
              borderRadius: "8px",
              cursor: "pointer",
              backgroundColor: "transparent",
            }}
          />
          <input
            type="text"
            value={newItem.color || "#ffffff"}
            onChange={(e) => setNewItem({ ...newItem, color: e.target.value })}
            placeholder="#ffffff"
            style={{
              flex: 1,
              padding: "10px 12px",
              borderRadius: "8px",
              border: "1px solid #d1d5db",
              backgroundColor: "white",
              fontSize: "14px",
              color: "#374151",
              outline: "none",
              transition: "border-color 0.2s",
            }}
            onFocus={(e) => (e.target.style.borderColor = "#1a7a73")}
            onBlur={(e) => (e.target.style.borderColor = "#d1d5db")}
          />
        </div>
      </div>
    </>
  );
};

export default CardEditPopup;
